import { FactProcess, StaffUser, StaffRole } from '../types';

const DB_NAME = 'LluisLawDB';
const DB_VERSION = 3; 
const STORE_CASES = 'cases';
const STORE_STAFF = 'staff';

export const CaseDatabase = {
  async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains(STORE_CASES)) {
          const store = db.createObjectStore(STORE_CASES, { keyPath: 'id' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }
        
        if (!db.objectStoreNames.contains(STORE_STAFF)) {
          const staffStore = db.createObjectStore(STORE_STAFF, { keyPath: 'id' });
          staffStore.createIndex('email', 'email', { unique: true });
        }
      };
    });
  },

  // Case Methods
  async getAll(): Promise<FactProcess[]> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_CASES], 'readonly');
        const store = transaction.objectStore(STORE_CASES);
        const request = store.getAll();
        request.onsuccess = () => {
           const res = request.result as FactProcess[];
           res.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
           resolve(res);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      console.error("Database getAll failure:", e);
      return [];
    }
  },

  async save(process: FactProcess): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_CASES], 'readwrite');
      const store = transaction.objectStore(STORE_CASES);
      const p = { ...process, lastUpdated: new Date().toISOString() };
      const request = store.put(p);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  // Staff Methods
  async getAllStaff(): Promise<StaffUser[]> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_STAFF], 'readonly');
        const store = transaction.objectStore(STORE_STAFF);
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      console.error("Database getAllStaff failure:", e);
      return [];
    }
  },

  async getStaffByEmail(email: string): Promise<StaffUser | undefined> {
    const staff = await this.getAllStaff();
    return staff.find(s => s.email.toLowerCase() === email.toLowerCase());
  },

  async saveStaff(user: StaffUser): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_STAFF], 'readwrite');
      const store = transaction.objectStore(STORE_STAFF);
      const request = store.put(user);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async deleteStaff(id: string): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_STAFF], 'readwrite');
      const store = transaction.objectStore(STORE_STAFF);
      
      const request = store.delete(id);
      
      transaction.oncomplete = () => {
        console.log(`Staff ID ${id} deleted successfully.`);
        resolve();
      };
      
      transaction.onerror = () => {
        console.error("Delete Staff Transaction Error:", transaction.error);
        reject(transaction.error);
      };
      
      request.onerror = () => {
        console.error("Delete Staff Request Error:", request.error);
        reject(request.error);
      };
    });
  }
};